import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CreatePartnerDealRequest } from '@interfaces/partners/requests/partner-deal.request';
import {
  PartnerCnpjLookupResponse,
  PartnerDealDocumentResponse,
  PartnerDealResponse,
} from '@interfaces/partners/responses/partner-deal.response';
import { Roles } from '@app/core/guard/Roles';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { MenuItem } from 'primeng/api';
import { Observable, Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  finalize,
  first,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { RegisterDealLeaveGuarded } from './register-deal-leave.guard';
import {
  PARTNER_TERM_YEARS_OPTIONS,
  parsePartnerTermYears,
  partnerTermYearsLabel,
} from '@src/app/shared/utils/partner-term-years.utils';
import { PartnerFlatFeeSimulationResponse } from '@interfaces/partners/responses/partner-flat-fee.response';

/** Espera entre a última tecla no valor e a simulação do flat fee no backend. */
const FLAT_FEE_SIMULATION_DEBOUNCE_MS = 300;

/**
 * Intervalo do polling do andamento dos documentos.
 *
 * 5s é o compromisso entre "parece vivo" e não martelar a API: a classificação leva
 * dezenas de segundos, então intervalo menor só geraria request repetido com a mesma
 * resposta.
 */
const PollingIntervalMs = 5000;

/**
 * Formatos aceitos na documentação do deal.
 *
 * Espelha o `AllowedFileExtensions` do backend (`Legatus.Vault.Application.Commom.Utils.Web`),
 * que é quem recusa o upload de verdade — recortado no que faz sentido como documentação
 * contábil. Ao mexer aqui, confira lá primeiro: liberar na tela o que o backend recusa
 * significa a pessoa escolher o arquivo e só descobrir o problema depois do envio.
 */
const FormatosAceitos = [
  '.pdf',
  '.xls',
  '.xlsx',
  '.xlsm',
  '.csv',
  '.doc',
  '.docx',
  '.jpg',
  '.jpeg',
  '.png',
];

/**
 * Arquivo saindo do navegador para o deal (card 3670).
 *
 * `done` liga quando o POST volta: o arquivo continua na lista como "Enviando" até a próxima
 * leitura dos documentos trazer a linha do servidor, para não sumir e reaparecer.
 */
interface SendingFile {
  file: File;
  done: boolean;
}

/**
 * Teto de arquivos por deal.
 *
 * Folgado de propósito: balanço, DRE e balancete de três exercícios já passam de dez
 * arquivos, e o padrão do componente (7) recusaria o envio no meio.
 */
const MaxArquivos = 30;

/**
 * Tela "Registrar Deal" — primeira tela real do módulo Comercial (card 2902, etapa A).
 *
 * Fluxo em dois tempos, de propósito:
 *
 *   1. O parceiro digita o CNPJ e **consulta**. A resposta traz a razão social da Receita
 *      e diz se o CNPJ está livre. É aqui que ele descobre cedo que o cliente já está em
 *      atendimento, sem preencher o resto do formulário à toa.
 *   2. Com o CNPJ livre, ele informa o valor e **registra**. O backend cria a empresa,
 *      reserva o CNPJ por 90 dias e grava o deal na mesma transação.
 *
 * A consulta é uma checagem de tela, não a garantia: entre um passo e o outro, outro
 * parceiro pode reservar o mesmo CNPJ. Quem garante a exclusividade é o índice único no
 * banco — e é por isso que o registro também pode recusar com a mesma mensagem.
 */
@Component({
  selector: 'app-register-deal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
  templateUrl: './register-deal.component.html',
  styleUrls: ['./register-deal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterDealComponent
  extends VaultBaseComponent
  implements OnInit, OnDestroy, RegisterDealLeaveGuarded
{
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  form!: FormGroup;

  /**
   * CNPJ digitado, só os dígitos.
   *
   * É sinal, e não um getter em cima do form: com `OnPush`, o botão de consulta precisa
   * reavaliar a cada tecla, e `valueChanges` sozinho não marca a view para checagem.
   */
  readonly cnpjDigits = signal('');

  /** Resultado da última consulta de CNPJ. Nulo enquanto ninguém consultou. */
  readonly lookup = signal<PartnerCnpjLookupResponse | null>(null);
  readonly lookingUp = signal(false);
  readonly saving = signal(false);

  /**
   * Quem registra é o time interno da Legatus (card 3169). Deal da Legatus não reserva CNPJ, então
   * os textos que prometem reserva são só do parceiro. Nasce falso: a tela abre na visão do
   * parceiro, que é o caso mais comum.
   */
  readonly visaoInterna = signal(false);

  /** Deal recém-criado. Enquanto existir, a tela mostra a confirmação no lugar do formulário. */
  readonly createdDeal = signal<PartnerDealResponse | null>(null);

  /** Opções do "Prazo da captação" (1 a 5 anos, card 3683). */
  readonly termYearsOptions = PARTNER_TERM_YEARS_OPTIONS;
  readonly termYearsLabel = partnerTermYearsLabel;

  /**
   * Flat fee estimado do valor e prazo digitados (card 3683), simulado no backend (card 3707) a
   * cada mudança do formulário. Nulo sem prazo (ele é opcional) ou se a simulação falhar, e aí a
   * linha some: é informativa e não trava o registro.
   */
  readonly flatFeeEstimate = signal<PartnerFlatFeeSimulationResponse | null>(null);

  /** Documentos do deal recém-criado, com o andamento de cada um. */
  readonly documents = signal<PartnerDealDocumentResponse[]>([]);
  readonly uploading = signal(false);

  /** Arquivos cujo envio ainda não terminou de ser refletido na lista (card 3670). */
  readonly sendingFiles = signal<SendingFile[]>([]);

  /** Corta os envios em curso quando a pessoa confirma sair sem esperar (card 3670). */
  private readonly cancelUploads$ = new Subject<void>();

  /** Handle do polling. Só existe enquanto há documento em andamento. */
  private pollingHandle: ReturnType<typeof setInterval> | null = null;

  constructor(
    translateService: TranslateService,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
    private readonly toast: ToastHelperService,
    private readonly formErrorHandler: FormErrorHandlerService,
    private readonly partnersService: PartnersHttpService,
    private readonly permissionsService: PermissionsService,
    private readonly confirmationService: ConfirmationService
  ) {
    super(translateService);
    this.buildForm();
  }

  /** Monta o breadcrumb e invalida a consulta sempre que o CNPJ muda. */
  ngOnInit(): void {
    this.breadcrumbs = [{ label: 'Comercial' }, { label: 'Registrar Deal' }];

    this.permissionsService
      .hasRole([Roles.TENANT, Roles.ADMIN])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(interno => {
        this.visaoInterna.set(interno);
        this.cdr.markForCheck();
      });

    // Trocar o CNPJ depois de consultar tem que apagar o resultado anterior: senão o
    // parceiro registraria o deal de um CNPJ com a razão social de outro na tela.
    this.form
      .get('cnpj')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(valor => {
        const digits = (valor ?? '').toString().replace(/\D/g, '');
        const changed = digits !== this.cnpjDigits();
        this.cnpjDigits.set(digits);
        if (!changed) return;

        if (this.lookup() !== null) this.lookup.set(null);
        this.discardPendingFilesForNewCnpj();
        this.cdr.markForCheck();
      });

    // Flat fee estimado ao vivo (card 3683): valor ou prazo mudou, a linha informativa acompanha.
    // A conta é do backend (card 3707); o switchMap descarta a resposta de uma digitação antiga.
    this.form.valueChanges
      .pipe(
        map(() => this.form.getRawValue()),
        debounceTime(FLAT_FEE_SIMULATION_DEBOUNCE_MS),
        switchMap(({ amount, termYears }) => {
          const term = parsePartnerTermYears(termYears);
          const value = Number(amount);
          if (term === null) return of(null);
          return this.partnersService
            .simulateFlatFee(Number.isFinite(value) && value > 0 ? value : 0, term)
            .pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(estimate => {
        this.flatFeeEstimate.set(estimate);
        this.cdr.markForCheck();
      });
  }

  /**
   * Desarma o polling ao sair da tela — senão ele sobreviveria à navegação.
   *
   * `override` + `super`: a `VaultBaseComponent` já implementa `ngOnDestroy` e tem a
   * própria limpeza para fazer; esquecer o `super` trocaria um vazamento por outro.
   */
  override ngOnDestroy(): void {
    this.stopPolling();
    super.ngOnDestroy();
  }

  /**
   * A área de documentos só aceita arquivo com o CNPJ verificado e livre, ou com o deal já criado
   * (card 3670). Antes disso a pessoa ainda não sabe se o deal vai existir.
   */
  get canAttachFiles(): boolean {
    return !!this.createdDeal() || !!this.lookup()?.isAvailable;
  }

  /** Arquivos que ainda estão saindo do navegador: sair agora cancela o envio deles. */
  get unsentUploadCount(): number {
    return this.sendingFiles().filter(f => !f.done).length;
  }

  /** Arquivos escolhidos antes do registro que se perdem se a pessoa sair sem registrar. */
  get unregisteredFileCount(): number {
    return this.createdDeal() ? 0 : this.pendingFiles().length;
  }

  /**
   * Pergunta antes de sair com documento que ainda não chegou ao servidor (card 3670). Usado pelo
   * guard da rota e pelo botão "Registrar outro deal". Depois que o arquivo subiu, a triagem segue no
   * servidor e a saída é livre.
   */
  confirmLeave(): boolean | Observable<boolean> {
    const sending = this.unsentUploadCount;
    const waiting = this.unregisteredFileCount;
    if (sending === 0 && waiting === 0) return true;

    const description =
      sending > 0
        ? `Ainda estamos enviando ${sending} ${sending === 1 ? 'documento' : 'documentos'} deste deal. ` +
          'Se sair agora, o envio é cancelado e você precisa mandar de novo pela aba Documentos do deal.'
        : `${waiting === 1 ? 'O documento escolhido ainda não foi enviado' : 'Os documentos escolhidos ainda não foram enviados'} ` +
          'porque o deal não foi registrado. Se sair agora, eles não são guardados.';

    return this.confirmationService
      .confirm(
        sending > 0 ? 'Aguarde o envio dos documentos' : 'Documentos não enviados',
        description,
        'danger',
        'Continuar aqui',
        sending > 0 ? 'Sair e cancelar o envio' : 'Sair sem enviar'
      )
      .pipe(first());
  }

  /** Fechar ou recarregar a aba com envio em curso: o navegador mostra o aviso dele (card 3670). */
  @HostListener('window:beforeunload', ['$event'])
  protected onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.unsentUploadCount > 0 || this.unregisteredFileCount > 0) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  /** O botão de consulta só acende com o CNPJ inteiro. Até lá fica no cinza do desabilitado. */
  get canLookup(): boolean {
    return this.cnpjDigits().length === 14;
  }

  /**
   * Indica se o formulário já pode ser enviado (CNPJ consultado, livre, e valor informado). O prazo
   * da captação é opcional (decisão do Breno no card 3683): não entra aqui.
   */
  get canSubmit(): boolean {
    return !!this.lookup()?.isAvailable && !!this.form.get('amount')?.valid;
  }

  /** Consulta o CNPJ na Receita e verifica a disponibilidade da reserva. */
  lookupCnpj(): void {
    const cnpj = (this.form.get('cnpj')?.value ?? '').toString().replace(/\D/g, '');

    if (cnpj.length !== 14) {
      this.form.get('cnpj')?.markAsTouched();
      this.toast.showToastError('Informe um CNPJ com 14 dígitos.');
      return;
    }

    this.lookingUp.set(true);
    this.lookup.set(null);

    this.partnersService
      .lookupCnpj(cnpj)
      .pipe(
        finalize(() => {
          this.lookingUp.set(false);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => {
          if (!result) return;
          this.lookup.set(result);
        },
        error: error => {
          this.formErrorHandler.showToast(error, 'Não foi possível consultar o CNPJ.');
        },
      });
  }

  /**
   * Registra o deal com o CNPJ consultado e o valor informado.
   *
   * A guarda do `saving()` não é redundante com o `[disabled]` do botão: o clique é um
   * evento só, e enquanto ele não termina o `disabled` ainda não valeu. Sem a guarda,
   * qualquer segundo canal ligado ao mesmo clique (foi o caso do `(sendButtonClick)`
   * somado ao `type="submit"`) dispara um segundo POST — e no deal originado pela
   * Legatus, que não reserva CNPJ, nada no backend impede o duplicado.
   */
  submitAction(): void {
    if (this.saving()) return;

    if (!this.canSubmit) {
      this.form.markAllAsTouched();
      this.toast.showToastError('Consulte um CNPJ disponível e informe o valor da operação.');
      return;
    }

    const payload: CreatePartnerDealRequest = {
      cnpj: this.lookup()!.cnpj,
      amount: Number(this.form.get('amount')?.value),
      currencyCode: 'BRL',
      // O select entrega string; sem prazo (opção vazia) vai `null`.
      termYears: parsePartnerTermYears(this.form.get('termYears')?.value),
    };

    this.saving.set(true);

    this.partnersService
      .createDeal(payload)
      .pipe(
        finalize(() => {
          this.saving.set(false);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: deal => {
          if (!deal) return;
          this.createdDeal.set(deal);
          this.toast.showToastSuccess(
            deal.reservationExpiresAt
              ? 'Deal registrado e CNPJ reservado para você.'
              : 'Deal registrado.'
          );

          // Os arquivos soltos antes do registro sobem agora, sem a pessoa ter de
          // arrastar de novo. É o que faz a área de arraste valer desde o começo.
          const emEspera = this.pendingFiles();
          if (emEspera.length > 0) {
            this.pendingFiles.set([]);
            this.uploadFiles(emEspera);
          }
        },
        error: error => {
          this.formErrorHandler.showToast(error, 'Não foi possível registrar o deal.');
        },
      });
  }

  /**
   * Volta ao formulário em branco para registrar o próximo deal.
   *
   * Com envio em curso, pergunta antes (card 3670): limpar a tela no meio do envio esconderia o
   * resultado. Confirmando, os envios em curso são cancelados.
   */
  registerAnother(): void {
    const answer = this.confirmLeave();
    if (answer === true) {
      this.resetForAnotherDeal();
      return;
    }
    if (answer === false) return;

    answer.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(leave => {
      if (leave) this.resetForAnotherDeal();
    });
  }

  /** Limpa a tela para o próximo deal, cancelando o que ainda estiver subindo. */
  private resetForAnotherDeal(): void {
    this.cancelUploads$.next();
    this.sendingFiles.set([]);
    this.uploading.set(false);
    this.stopPolling();
    this.createdDeal.set(null);
    this.lookup.set(null);
    this.documents.set([]);
    this.pendingFiles.set([]);
    this.form.reset({ cnpj: '', amount: null, termYears: null });
    this.cdr.markForCheck();
  }

  /** Arquivos escolhidos antes de o deal existir. Sobem sozinhos assim que ele nascer. */
  readonly pendingFiles = signal<File[]>([]);

  /** Régua de formatos e teto de arquivos, expostos para o template. */
  readonly formatosAceitos = FormatosAceitos;
  readonly maxFiles = MaxArquivos;
  readonly mensagemArquivoInvalido = `Envie um arquivo ${FormatosAceitos.join(', ')} de até 100 MB.`;

  /**
   * Recebe os arquivos já validados pelo `app-document-attachment`.
   *
   * O componente é sem estado no tema `dropzone`: ele valida formato e tamanho, avisa
   * sobre o que recusou e entrega aqui só os arquivos novos. Arrastar e clicar chegam
   * pelo mesmo caminho.
   */
  onFilesChosen(files: File[]): void {
    // Segunda trava, além da área desligada na tela: arquivo não entra sem CNPJ verificado.
    if (!this.canAttachFiles) return;
    this.receiveFiles(files);
  }

  /**
   * Trocar o CNPJ antes do registro descarta os arquivos em espera (decisão do Breno, 22/10/2026,
   * card 3670): eles podiam ser da empresa anterior e iriam para o deal errado.
   */
  private discardPendingFilesForNewCnpj(): void {
    if (this.createdDeal() || this.pendingFiles().length === 0) return;

    this.pendingFiles.set([]);
    this.toast.showToastWarn(
      'Documentos retirados',
      'O CNPJ mudou, então os documentos escolhidos foram retirados. Verifique o novo CNPJ e escolha os documentos de novo.'
    );
  }

  /** Descarta um arquivo que ainda está em espera. */
  removePendingFile(index: number): void {
    this.pendingFiles.update(atual => atual.filter((_, i) => i !== index));
    this.cdr.markForCheck();
  }

  /**
   * Encaminha os arquivos: sobe agora se o deal já existe, senão guarda.
   *
   * O upload precisa de um deal (`POST deals/{id}/documents`), mas a tela mostra a área
   * de arraste desde o começo — o desenho de 10/09/2026 pede que subir a documentação
   * seja o gesto de abertura. Guardar e enviar assim que o deal nasce entrega isso sem
   * inventar um endpoint que aceite arquivo solto.
   */
  private receiveFiles(files: File[]): void {
    if (files.length === 0) return;

    if (!this.createdDeal()) {
      this.pendingFiles.update(atual => [...atual, ...files]);
      this.cdr.markForCheck();
      return;
    }

    this.uploadFiles(files);
  }

  /**
   * Sobe os arquivos, um a um.
   *
   * O backend responde antes de classificar; quem conta o resto da história é o polling.
   */
  private uploadFiles(files: File[]): void {
    const deal = this.createdDeal();
    if (!deal || files.length === 0) return;

    this.uploading.set(true);
    this.sendingFiles.update(current => [
      ...current,
      ...files.map(file => ({ file, done: false })),
    ]);
    this.cdr.markForCheck();

    // Cada arquivo que termina já entra na lista como "Na fila"; não espera o lote inteiro.
    const finished = (file: File, ok: boolean) => {
      this.sendingFiles.update(current =>
        ok
          ? current.map(f => (f.file === file ? { ...f, done: true } : f))
          : current.filter(f => f.file !== file)
      );
      if (this.unsentUploadCount === 0) this.uploading.set(false);
      this.refreshDocuments();
      this.cdr.markForCheck();
    };

    for (const file of files) {
      this.partnersService
        .uploadDealDocument(deal.id, file)
        .pipe(takeUntil(this.cancelUploads$), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => finished(file, true),
          error: error => {
            this.formErrorHandler.showToast(error, `Não foi possível enviar ${file.name}.`);
            finished(file, false);
          },
        });
    }
  }

  /** Indica se ainda há documento em andamento — é o que mantém o polling vivo. */
  get temDocumentoEmAndamento(): boolean {
    return this.documents().some(d => d.status === 'Pending' || d.status === 'Processing');
  }

  /** Rótulo do andamento, na linguagem que o card pediu. */
  statusLabel(status: PartnerDealDocumentResponse['status']): string {
    switch (status) {
      case 'Pending':
        return 'Na fila';
      case 'Processing':
        return 'Organizando os documentos…';
      case 'Completed':
        return 'Pronto';
      default:
        return 'Falhou';
    }
  }

  /**
   * Relê os documentos e (re)arma o polling enquanto houver algum em andamento.
   */
  private refreshDocuments(): void {
    const deal = this.createdDeal();
    if (!deal) return;

    this.partnersService
      .getDealDocuments(deal.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: docs => {
          this.documents.set(docs ?? []);
          // A linha do servidor chegou: o arquivo deixa de aparecer como "Enviando". Só sai quem já
          // está na resposta: cada envio dispara a sua leitura, e uma leitura antiga que volte depois
          // não pode tirar da lista um arquivo que ela ainda não traz.
          const arrivedNames = new Set((docs ?? []).map(d => d.fileName));
          this.sendingFiles.update(current =>
            current.filter(f => !f.done || !arrivedNames.has(f.file.name))
          );
          // Para sozinho quando tudo termina: polling eterno numa tela aberta o dia
          // inteiro é request à toa contra a API.
          if (this.temDocumentoEmAndamento) this.startPolling();
          else this.stopPolling();
          this.cdr.markForCheck();
        },
        error: () => {
          this.stopPolling();
          // O envio terminou; sem a leitura, não deixa o arquivo preso em "Enviando".
          this.sendingFiles.update(current => current.filter(f => !f.done));
          this.cdr.markForCheck();
        },
      });
  }

  /** Arma o polling, se ainda não estiver armado. */
  private startPolling(): void {
    if (this.pollingHandle) return;
    this.pollingHandle = setInterval(() => this.refreshDocuments(), PollingIntervalMs);
  }

  /** Desarma o polling. Idempotente. */
  private stopPolling(): void {
    if (!this.pollingHandle) return;
    clearInterval(this.pollingHandle);
    this.pollingHandle = null;
  }

  /** Campos do formulário. Moeda não entra: nesta v1 todo deal é em BRL. */
  private buildForm(): void {
    this.form = this.fb.group({
      cnpj: ['', [Validators.required]],
      amount: [null, [Validators.required, Validators.min(0.01)]],
      // Prazo da captação (card 3683): opcional. Sem prazo, o deal nasce com `termYears` nulo.
      termYears: [null],
    });
  }
}
